import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  TextField,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  PersonAdd,
  Security,
  VpnKey,
  ContentCopy,
  PersonOff,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { displayName } from '../../types';
import AccessControlModal from '../../components/AccessControlModal';

interface AccessControlEntry {
  type: 'employee' | 'standalone';
  // Employee info
  employeeId?: string;
  employee?: {
    firstName: string;
    lastName: string;
    fullName: string;
    nickname?: string;
    email?: string;
  };
  hasAccount: boolean;
  // User account info (null if no account)
  id?: string;
  email: string;
  role?: string;
  isActive?: boolean;
  lastLoginAt?: string;
  createdAt?: string;
  displayPassword?: string;
}

const AccessControl: React.FC = () => {
  const [entries, setEntries] = useState<AccessControlEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; userId: string; userName: string }>({ open: false, userId: '', userName: '' });

  useEffect(() => {
    fetchAccessControl();
  }, []);

  const fetchAccessControl = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAccessControlView();
      setEntries(data);
    } catch (err: any) {
      console.error('Error fetching access control:', err);
      setError('Failed to load access control data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (userId: string) => {
    const entry = entries.find(e => e.id === userId);
    setConfirmDelete({ open: true, userId, userName: entry?.email || '' });
  };

  const handleConfirmDeleteUser = async () => {
    const { userId } = confirmDelete;
    setConfirmDelete({ open: false, userId: '', userName: '' });
    try {
      await apiService.deleteUser(userId);
      fetchAccessControl();
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleEditUser = (entry: AccessControlEntry) => {
    if (!entry.hasAccount || !entry.id) return;
    // Build a user-shaped object for the modal
    setEditingUser({
      id: entry.id,
      email: entry.email,
      role: entry.role,
      isActive: entry.isActive,
      employeeId: entry.employeeId,
      employee: entry.employee,
      lastLoginAt: entry.lastLoginAt,
      createdAt: entry.createdAt,
    });
    setIsModalOpen(true);
  };

  const handleCreateAccountForEmployee = (entry: AccessControlEntry) => {
    // Pre-fill the modal with employee email + linked employeeId
    setEditingUser(null);
    // We pass a partial "new user" hint via a dedicated state key handled in the modal
    setIsModalOpen(true);
    // Store the employee context so the modal can pre-fill it
    setTimeout(() => {
      // Trigger pre-fill by storing in editingUser with a special flag
      setEditingUser({
        _prefill: true,
        email: entry.employee?.email || entry.email,
        employeeId: entry.employeeId,
        role: 'employee',
      });
    }, 0);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const handleUserSaved = (newUser?: any) => {
    if (newUser && newUser.generatedPassword) {
      fetchAccessControl().then(() => {
        setEntries(current =>
          current.map(entry =>
            entry.email === newUser.email
              ? { ...entry, displayPassword: newUser.generatedPassword }
              : entry
          )
        );
      });
    } else {
      fetchAccessControl();
    }
  };

  const handleResetPassword = async (userId: string, userEmail: string) => {
    if (!window.confirm(`Reset password for ${userEmail}?`)) return;
    try {
      const result = await apiService.resetUserPassword(userId);
      setEntries(current =>
        current.map(entry =>
          entry.id === userId
            ? { ...entry, displayPassword: result.newPassword }
            : entry
        )
      );
    } catch (err: any) {
      console.error('Error resetting password:', err);
      setError(err.message || 'Failed to reset password');
    }
  };

  const getRoleColor = (role?: string) => {
    switch (role) {
      case 'hr_admin': return 'error';
      case 'employee': return 'primary';
      default: return 'default';
    }
  };

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'hr_admin': return 'HR Admin';
      case 'employee': return 'Employee';
      default: return role ? role.toUpperCase() : '-';
    }
  };

  const getDisplayName = (entry: AccessControlEntry): string => {
    if (entry.employee) {
      return entry.employee.nickname || entry.employee.fullName || `${entry.employee.firstName} ${entry.employee.lastName}`.trim();
    }
    return 'No Employee Linked';
  };

  const filteredEntries = entries.filter(entry => {
    const term = searchTerm.toLowerCase();
    return (
      entry.email.toLowerCase().includes(term) ||
      (entry.employee?.firstName || '').toLowerCase().includes(term) ||
      (entry.employee?.lastName || '').toLowerCase().includes(term) ||
      (entry.employee?.nickname || '').toLowerCase().includes(term)
    );
  });

  const stats = {
    totalEmployees: entries.filter(e => e.type === 'employee').length,
    withAccount: entries.filter(e => e.hasAccount).length,
    withoutAccount: entries.filter(e => e.type === 'employee' && !e.hasAccount).length,
    adminUsers: entries.filter(e => e.role === 'hr_admin').length,
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Access Control
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage user accounts and system access permissions
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => { setEditingUser(null); setIsModalOpen(true); }}
        >
          Create User Account
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Security sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4" color="primary">
                    {stats.totalEmployees}
                  </Typography>
                  <Typography color="text.secondary">Total Employees</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PersonAdd sx={{ mr: 2, color: 'success.main' }} />
                <Box>
                  <Typography variant="h4" color="success.main">
                    {stats.withAccount}
                  </Typography>
                  <Typography color="text.secondary">Have Accounts</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PersonOff sx={{ mr: 2, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {stats.withoutAccount}
                  </Typography>
                  <Typography color="text.secondary">No Account Yet</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Security sx={{ mr: 2, color: 'error.main' }} />
                <Box>
                  <Typography variant="h4" color="error.main">
                    {stats.adminUsers}
                  </Typography>
                  <Typography color="text.secondary">HR Admins</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
        </Grid>
      </Grid>

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Default Password</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEntries.map((entry, idx) => (
                <TableRow
                  key={entry.id || `emp-${entry.employeeId || idx}`}
                  hover
                  sx={!entry.hasAccount ? { bgcolor: 'action.hover' } : undefined}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body1">
                        {getDisplayName(entry)}
                      </Typography>
                      {entry.employee && (
                        <Typography variant="caption" color="text.secondary">
                          {entry.employee.fullName || `${entry.employee.firstName} ${entry.employee.lastName}`.trim()}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{entry.email}</TableCell>
                  <TableCell>
                    {entry.hasAccount && entry.role ? (
                      <Chip
                        label={getRoleLabel(entry.role)}
                        color={getRoleColor(entry.role)}
                        size="small"
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {entry.hasAccount ? (
                      <Chip
                        label={entry.isActive ? 'Active' : 'Inactive'}
                        color={entry.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    ) : (
                      <Chip label="No Account" color="warning" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {entry.hasAccount
                      ? entry.lastLoginAt
                        ? new Date(entry.lastLoginAt).toLocaleDateString()
                        : 'Never'
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {entry.hasAccount && entry.createdAt
                      ? new Date(entry.createdAt).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {entry.displayPassword ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Paper
                          sx={{
                            px: 2,
                            py: 1,
                            bgcolor: 'success.light',
                            color: 'success.contrastText',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            minWidth: '120px',
                            textAlign: 'center',
                          }}
                        >
                          {entry.displayPassword}
                        </Paper>
                        <IconButton
                          size="small"
                          onClick={() => navigator.clipboard.writeText(entry.displayPassword!)}
                          title="Copy password"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : entry.hasAccount ? (
                      <Typography color="text.secondary" variant="caption">
                        Click reset to generate
                      </Typography>
                    ) : (
                      <Typography color="text.secondary" variant="caption">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {entry.hasAccount && entry.id ? (
                      <>
                        <Tooltip title="Edit user">
                          <IconButton size="small" onClick={() => handleEditUser(entry)}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reset password">
                          <IconButton
                            size="small"
                            onClick={() => handleResetPassword(entry.id!, entry.email)}
                            color="warning"
                          >
                            <VpnKey />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete user">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUser(entry.id!)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip title="Create account for this employee">
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<PersonAdd />}
                          onClick={() => handleCreateAccountForEmployee(entry)}
                        >
                          Create Account
                        </Button>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filteredEntries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">
                      {searchTerm ? 'No users found matching your search.' : 'No records found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          Total: {filteredEntries.length} records
          {searchTerm && ` (filtered from ${entries.length})`}
        </Typography>
      </Box>

      <AccessControlModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onUserCreated={handleUserSaved}
        editingUser={editingUser}
      />

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, userId: '', userName: '' })}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          Are you sure you want to delete <strong>{confirmDelete.userName}</strong>? This cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, userId: '', userName: '' })}>Cancel</Button>
          <Button onClick={handleConfirmDeleteUser} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessControl;
